/**
 * Роль человека: одно место, где она читается и пишется (профиль, `pages/profile-page.ts`). Роль
 * выбирается ТОЛЬКО в профиле — кнопку-роль в боковом меню владелец убрал 19.10.2026 («вот эту жёлтую
 * плашку слева убирай нахрен. Роль выбирается в настройках профиля и всё», SCRUM-670).
 *
 * Роль и компания лежат в одном `PUT /users/me/work`, который заменяет ОБЕ записи. Поэтому здесь
 * хранится пара целиком: роль уходит вместе с последней известной компанией, а пока пару не прочли —
 * сначала читаем (пустая компания не должна затереть настоящую).
 *
 * Помнится по id человека: тот, кто вошёл следом в том же браузере, чужой роли не увидит.
 */
import { getWork, updateWork, type UserWork } from "../api/account-api";
import { getStoredUser, updateStoredUserRole } from "../api/http";

type Listener = (role: string) => void;

let cache: { userId: string; work: UserWork } | undefined;
let inflight: { userId: string; promise: Promise<UserWork> } | undefined;
const listeners = new Set<Listener>();

const currentUserId = (): string | undefined => getStoredUser()?.id;

function known(): { userId: string; work: UserWork } | undefined {
  return cache && cache.userId === currentUserId() ? cache : undefined;
}

function remember(userId: string, work: UserWork): void {
  const before = cache?.userId === userId ? cache.work.userRole : undefined;
  cache = { userId, work: { company: work.company, userRole: work.userRole, ...(work.warehouseAddress === undefined ? {} : { warehouseAddress: work.warehouseAddress }) } };
  // Выбор первого экрана после входа читает роль из сохранённого человека (`app-shell.ts`), а не отсюда.
  updateStoredUserRole(work.userRole);
  if (before !== work.userRole) for (const listener of [...listeners]) listener(work.userRole);
}

/** Роль, если она уже прочитана для ТЕКУЩЕГО человека; иначе `undefined` («ещё не знаем»). */
export function getKnownRole(): string | undefined {
  return known()?.work.userRole;
}

/** Читает рабочие данные один раз на человека; параллельные вызовы делят один запрос. */
export async function loadRole(): Promise<string> {
  const hit = known();
  if (hit) return hit.work.userRole;
  const userId = currentUserId();
  if (!userId) return "";
  if (!inflight || inflight.userId !== userId) {
    inflight = { userId, promise: getWork() };
  }
  const pending = inflight;
  try {
    const work = await pending.promise;
    if (currentUserId() === userId) remember(userId, work);
    return work.userRole;
  } finally {
    if (inflight === pending) inflight = undefined;
  }
}

/**
 * Сохраняет роль тем же `PUT /users/me/work`. Не записалось — выбрасывает, а в памяти и у
 * подписчиков остаётся прежнее: показывать то, чего на сервере нет, нельзя.
 */
export async function saveRole(role: string): Promise<void> {
  await loadRole();
  const hit = known();
  const userId = currentUserId();
  if (!hit || !userId) throw new Error("not signed in");
  const saved = await updateWork({ company: hit.work.company, userRole: role });
  remember(userId, saved);
}

/**
 * Сохраняет компанию, не трогая роль. Сначала читает пару: профиль, у которого роль ещё не
 * прочиталась, иначе отправил бы пустую и стёр бы её на сервере (проверка PR 488).
 *
 * Адрес склада (SCRUM-851) уходит вместе с компанией, когда его передали: кнопка «Save» рабочих данных пишет оба поля.
 * Не передали - сервер адрес не трогает (как при выборе роли).
 */
export async function saveCompany(company: string, warehouseAddress?: string): Promise<UserWork> {
  await loadRole();
  const hit = known();
  const userId = currentUserId();
  if (!hit || !userId) throw new Error("not signed in");
  const saved = await updateWork({ company, userRole: hit.work.userRole, ...(warehouseAddress === undefined ? {} : { warehouseAddress }) });
  remember(userId, saved);
  return saved;
}

/** Профиль, прочитав или сохранив рабочие данные сам, выравнивает по ним и всех остальных. */
export function noteWork(work: UserWork): void {
  const userId = currentUserId();
  if (userId) remember(userId, work);
}

export function subscribeRole(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Только для тестов. */
export function resetUserRoleForTests(): void {
  cache = undefined;
  inflight = undefined;
  listeners.clear();
}
